<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import {
  FhButton,
  FhText,
  FhTextInput,
  FhLabel,
  FhCard,
} from '@fareharbor-com/beacon-vue';
import {
  type RequestPasswordResetPayload,
  postRequestPasswordReset,
} from '@/common/api/postRequestPasswordReset';
import { handleThrownError } from '@/common/errors/handleThrownError';
import { ValidationService } from '@/common/validation';
import DeskForm from '@/components/shared/DeskForm/DeskForm.vue';
import BaseFlexbox from '@/components/ui/BaseFlexbox/BaseFlexbox.vue';
import { config } from '@/configs';
import { InventoryEnvironment } from '@/types/InventoryEnvironment';

const currentEnvironment = config.ENVIRONMENT;
const isStagingOrLocal = computed(() =>
  [InventoryEnvironment.STAGING, InventoryEnvironment.LOCAL].includes(
    currentEnvironment,
  ),
);

const router = useRouter();
const email = ref('');
const isSubmitting = ref(false);
const successMessage = ref('');
const errorMessage = ref('');
const requestSent = ref(false);

const isFormValid = computed<boolean>(() => {
  const validation = ValidationService.validateEmail(email.value);
  return validation.isValid;
});

async function requestPasswordReset() {
  const validation = ValidationService.validateEmail(email.value);
  if (!validation.isValid) {
    errorMessage.value = validation.error;
    return;
  }

  isSubmitting.value = true;
  successMessage.value = '';
  errorMessage.value = '';

  try {
    const payload: RequestPasswordResetPayload = { email: email.value };
    const { error } = await postRequestPasswordReset(payload);

    if (error) {
      throw error;
    }

    requestSent.value = true;
    successMessage.value =
      'If an account with this email exists, you will receive a password reset email.';
  } catch (e) {
    handleThrownError({
      error: e,
      toastMessage: 'An error occurred. Please check your email and try again.',
      log: true,
    });
  } finally {
    isSubmitting.value = false;
  }
}

const topBlurClass = computed(() =>
  isStagingOrLocal.value ? 'top-10' : 'top-0',
);
const loginFormHeight = computed(() =>
  isStagingOrLocal.value ? 'calc(100vh - 165px)' : 'calc(100vh - 150px)',
);
</script>

<template>
  <BaseFlexbox :style="{ height: loginFormHeight }">
    <div
      :class="[
        'flex flex-col fixed inset-x-0 z-50 flex justify-center items-center w-full h-screen backdrop-blur-sm ',
        topBlurClass,
      ]"
    >
      <BaseFlexbox
        justifyContent="center"
        class="relative w-full max-w-md p-6 border-gray-300"
      >
        <img
          src="@/assets/images/logo.png"
          alt="Marketplace Logo"
          class="h-14"
        />
      </BaseFlexbox>

      <FhCard
        elevated
        id="password-reset-modal"
        data-test-id="password-reset-modal"
        size="lg"
        class="bg-white shadow-lg rounded-lg overflow-hidden w-full max-w-md"
      >
        <template v-if="!requestSent">
          <DeskForm
            form-name="Password Reset Form"
            submit-text="Send Reset Link"
            :errors="errorMessage ? [errorMessage] : []"
            :is-submitting="isSubmitting"
            :is-valid="isFormValid"
            @submit.prevent="requestPasswordReset"
            data-test-id="password-reset-form"
          >
            <BaseFlexbox direction="column" gap="3" class="mb-3 mt-4">
              <FhTextInput
                v-model="email"
                autocomplete="email"
                data-test-id="password-reset-email"
                size="md"
              >
                <FhLabel>Email</FhLabel>
              </FhTextInput>
            </BaseFlexbox>

            <template #form-actions>
              <FhButton
                :disabled="isSubmitting"
                :loading="isSubmitting"
                type="submit"
                fullWidth
                data-test-id="password-reset-button"
                class="mt-2"
              >
                Send password reset link
              </FhButton>
            </template>
          </DeskForm>
        </template>

        <template v-else>
          <BaseFlexbox
            direction="column"
            alignItems="center"
            gap="4"
            class="p-10"
          >
            <FhText
              variant="body-lg"
              alignment="center"
              class="font-medium text-gray-900 mb-2"
              style="font-size: 1.5rem"
            >
              Check Your Email
            </FhText>
            <FhText
              variant="body-md"
              alignment="center"
              class="text-gray-600 mb-6"
              data-test-id="password-reset-success-message"
            >
              If an account with this email exists, you will receive a password
              reset email.
            </FhText>
            <FhButton
              variant="primary"
              fullWidth
              @click="router.push({ name: 'login' })"
              data-test-id="back-to-login-button"
            >
              Back to Login
            </FhButton>
          </BaseFlexbox>
        </template>
      </FhCard>
    </div>
  </BaseFlexbox>
</template>
